import { useRef } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { useI18n } from "@/i18n";
import { useLandingNarrative } from "@/hooks/useLandingNarrative";
import { getNarrativeContent, narrativeScenes } from "@/lib/landingNarrative";
import { LandingNarrativeFrames } from "./LandingNarrativeFrames";
import layout from "./LandingVisual.module.css";
import styles from "./LandingNarrative.module.css";

export function LandingStory({ example: ex }) {
  const { t, lang } = useI18n();
  const { state, dispatch, staticMode, running, viewportRef } = useLandingNarrative();
  const stageRefs = useRef([]);
  const content = getNarrativeContent(ex, t);
  const select = (scene) => dispatch({ type: "scene", scene });
  const commitExample = () => {
    dispatch({ type: "commit" });
    stageRefs.current[4]?.focus();
  };
  return <section id="arsitektur-kognitif" className={layout.architecture} aria-labelledby="landing-layers" data-testid="landing-architecture">
    <div className={layout.container}>
      <div className={layout.sectionHeading}>
        <p className={layout.eyebrow}>{t("landing.visual.architecture")}</p>
        <h2 id="landing-layers">{t("landing.story.title")}</h2>
        <p className={layout.lede}>{t("landing.story.lede")}</p>
      </div>
      <div className={styles.shell} data-testid="landing-example" data-mode={staticMode ? "static" : "animated"} data-scene={state.scene} data-elapsed={Math.floor(state.elapsed)} data-running={running} onFocusCapture={(event) => {
        if (!event.target.hasAttribute("data-playback")) dispatch({ type: "pause" });
      }}>
        <p className={styles.caption}><span>{t("example.badge")}</span>{ex.title}</p>
        {staticMode ? <p className={styles.footnote}>{t("landing.narrative.static")}</p> : <>
          <ol className={styles.steps} aria-label={t("landing.narrative.controls")}>
            {narrativeScenes.map((step, index) => <li key={step.id}>
              <button type="button" className={styles.step} ref={(node) => { stageRefs.current[index] = node; }} aria-label={`${String(index + 1).padStart(2, "0")} ${t(step.label)}`} aria-current={state.scene === index ? "step" : undefined} aria-controls="narrative-stage" onClick={() => select(index)} data-testid={`narrative-step-${index}`}>
                <span>{String(index + 1).padStart(2, "0")}</span><span className={styles.longName}>{t(step.label)}</span><span className={styles.shortName}>{t(step.shortLabel || step.label)}</span>
              </button>
            </li>)}
          </ol>
          <div className={styles.transport}>
            <div className={styles.transportButtons}>
              <button type="button" className={styles.control} data-playback="true" onClick={() => dispatch({ type: "toggle" })} data-testid="narrative-playback">
                {state.playing ? <Pause size={16} aria-hidden="true" /> : <Play size={16} aria-hidden="true" />}{t(state.playing ? "landing.narrative.pause" : "landing.narrative.play")}
              </button>
              <button type="button" className={styles.control} aria-label={t("landing.narrative.previous")} disabled={state.scene === 0} onClick={() => select(state.scene - 1)} data-testid="narrative-previous"><ChevronLeft size={16} aria-hidden="true" /></button>
              <button type="button" className={styles.control} aria-label={t("landing.narrative.next")} disabled={state.scene === 4} onClick={() => select(state.scene + 1)} data-testid="narrative-next"><ChevronRight size={16} aria-hidden="true" /></button>
            </div>
            <span className={styles.status}>{t(state.playing ? "landing.narrative.running" : "landing.narrative.paused")}</span>
          </div>
          <div className={styles.progress} aria-hidden="true"><span style={{ transform: `scaleX(${(state.scene + state.elapsed / narrativeScenes[state.scene].duration) / narrativeScenes.length})` }} /></div>
        </>}
        <div className={staticMode ? undefined : styles.window} ref={viewportRef} id="narrative-stage">
          {!staticMode && state.scene > 0 && <div className={styles.context}>
            <p className={styles.contextQuestion} data-testid="landing-example-question">{content.question}</p>
            <span className={styles.horizon} data-testid="landing-example-horizon">{t("field.horizon")} · {t("horizon.days", { n: ex.horizonDays })}</span>
          </div>}
          <LandingNarrativeFrames {...{ content, t, lang, staticMode }} animated={!state.manual} horizon={ex.horizonDays} scene={state.scene} choice={state.choice} onChoice={(choice) => dispatch({ type: "choice", choice })} onCommit={commitExample} />
        </div>
        <p className={styles.footnote}>{t("landing.narrative.note")}</p>
      </div>
    </div>
  </section>;
}
